<template>
  <v-card flat color="transparent" class="note-item--headline d-flex align-start">
    <BulkSelectCheckbox
      v-if="isBulkActionsActive"
      class="mx-3 mt-0"
      :checked="isSelected"
      @click="toggleSelect"
    />
    <div class="full-width">
      <div class="text-neutral400 d-flex align-center mb-2 text-caption">
        <NoteSource :note="note" />
        <span class="mx-1">&middot;</span>
        <NoteDate :value="note.createdAt" />
      </div>

      <div
        class="note-headline pa-0 mb-3 text-body-1 font-weight-semibold text-neutral900"
        @click="onTitleClick"
      >
        {{ note.title || $t('common.untitled_note') }}
      </div>

      <NoteTags v-if="note.tags?.length" class="mb-3" :tags="note.tags" @tag-selected="onTagSelected" />

      <NoteActions
        :note="note"
        :user-id="userId"
        :public-mode="publicMode"
        :membership="membership"
        class="text-text"
        @favorite="$emit('favorite')"
        @save="$emit('save')"
        @add-to-collection="$emit('add-to-collection')"
        @add-to-collection-from-collection="$emit('add-to-collection-from-collection')"
        @comments="$emit('comments')"
        @like="$emit('like')"
        @likes="$emit('likes')"
        @pin-edit="$emit('pin-edit')"
      />
    </div>
  </v-card>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import NoteSource from './NoteSource.vue'
import NoteActions from './NoteActions.vue'
import NoteDate from './NoteDate.vue'
import NoteTags from './NoteTags.vue'
import { useBulkActionsV2 } from '@/composables/useBulkActionsV2'
import BulkSelectCheckbox from '@/components/BulkSelectCheckbox.vue'

const props = defineProps({
  note: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  publicMode: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'toggle-select',
  'tag-selected',
  'favorite',
  'save',
  'add-to-collection',
  'add-to-collection-from-collection',
  'comments',
  'like',
  'likes',
  'pin-edit',
])

const router = useRouter()
const route = useRoute()
const { isBulkActionsActive, isItemSelected } = useBulkActionsV2()

const isSelected = computed(() => {
  return isItemSelected(props.note.id)
})

function onTitleClick() {
  if (isBulkActionsActive.value) {
    toggleSelect()
  } else {
    navigateToDetail()
  }
}

function navigateToDetail() {
  if (props.publicMode) {
    const { username, id } = route.params
    router.push(`/u/${username}/collections/${id}/notes/${props.note.id}`)
    return
  }
  if (props.note.isPrivate) {
    router.push(`/notes/${props.note.id}`)
  } else {
    router.push(`/collections/${props.note.ownerId}/notes/${props.note.id}`)
  }
}

function toggleSelect() {
  emit('toggle-select', props.note)
}

function onTagSelected(tag) {
  emit('tag-selected', tag)
}
</script>

<style lang="scss" scoped>
.note-headline {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  overflow: hidden;
  cursor: pointer;
}
</style>
